import React, { useState } from 'react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * AutoSkillCard — generische Formular-Karte für Skills mit ui="auto".
 * Rendert Felder aus skill.fields[] und POSTet als FormData an /skills/{id}/execute.
 */
export default function AutoSkillCard({ skill }) {
  const { token } = useAuth();
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  function handleChange(name, value) {
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function handleFile(name, file) {
    setFiles(prev => ({ ...prev, [name]: file }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('loading');
    setResult(null);
    setError(null);

    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (v !== undefined && v !== '') fd.append(k, v);
    });
    Object.entries(files).forEach(([k, f]) => {
      if (f) fd.append(k, f);
    });

    try {
      const res = await fetch(`${API_URL}/skills/${skill.id}/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || data.message || `Fehler ${res.status}`);
        setStatus('error');
      } else {
        setResult(data);
        setStatus('done');
      }
    } catch (err) {
      setError(err.message || 'Netzwerkfehler');
      setStatus('error');
    }
  }

  function renderField(field) {
    const commonStyle = {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #ddd',
      borderRadius: 6,
      fontSize: 14,
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            key={field.name}
            required={field.required}
            placeholder={field.description || field.label}
            value={formData[field.name] || ''}
            onChange={e => handleChange(field.name, e.target.value)}
            rows={4}
            style={{ ...commonStyle, resize: 'vertical' }}
          />
        );
      case 'select':
        return (
          <select
            key={field.name}
            required={field.required}
            value={formData[field.name] || field.default || ''}
            onChange={e => handleChange(field.name, e.target.value)}
            style={commonStyle}
          >
            <option value="">— bitte wählen —</option>
            {(field.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'number':
        return (
          <input
            key={field.name}
            type="number"
            required={field.required}
            placeholder={field.description || field.label}
            value={formData[field.name] || ''}
            onChange={e => handleChange(field.name, e.target.value)}
            style={commonStyle}
          />
        );
      case 'file':
        return (
          <input
            key={field.name}
            type="file"
            required={field.required}
            accept={field.accept || undefined}
            onChange={e => handleFile(field.name, e.target.files[0] || null)}
            style={{ ...commonStyle, padding: '8px 12px' }}
          />
        );
      default: // text und Fallback
        return (
          <input
            key={field.name}
            type="text"
            required={field.required}
            placeholder={field.description || field.label}
            value={formData[field.name] || field.default || ''}
            onChange={e => handleChange(field.name, e.target.value)}
            style={commonStyle}
          />
        );
    }
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 0' }}>
      {/* Header */}
      <div style={{
        background: '#f8f7f5',
        border: '1px solid #e5e0d8',
        borderRadius: 10,
        padding: '20px 24px',
        marginBottom: 24,
      }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a2e4a' }}>
          {skill.name}
        </h2>
        {skill.description && (
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280' }}>
            {skill.description}
          </p>
        )}
      </div>

      {/* Formular */}
      <form onSubmit={handleSubmit}>
        {(skill.fields || []).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
            {skill.fields.map(field => (
              <div key={field.name}>
                <label style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 6,
                }}>
                  {field.label}
                  {field.required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
                </label>
                {renderField(field)}
                {field.description && field.type !== 'file' && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
                    {field.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <button
          type="submit"
          disabled={status === 'loading'}
          style={{
            padding: '12px 32px',
            background: status === 'loading' ? '#9ca3af' : '#1a2e4a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 700,
            cursor: status === 'loading' ? 'not-allowed' : 'pointer',
            letterSpacing: '0.3px',
          }}
        >
          {status === 'loading' ? 'Wird ausgeführt…' : 'Ausführen'}
        </button>
      </form>

      {/* Ergebnis */}
      {status === 'error' && error && (
        <div style={{
          marginTop: 20,
          padding: '14px 18px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 6,
          color: '#dc2626',
          fontSize: 14,
        }}>
          Fehler: {error}
        </div>
      )}

      {status === 'done' && result && (
        <div style={{
          marginTop: 20,
          padding: '14px 18px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 6,
          fontSize: 14,
          color: '#166534',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Erfolgreich ausgeführt</div>
          {result.summary && (
            <div style={{ marginBottom: 8, color: '#374151' }}>{result.summary}</div>
          )}
          {Array.isArray(result.files) && result.files.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
                Dateien:
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.files.map((f, i) => {
                  const name = typeof f === 'string' ? f.split('/').pop() : `Datei ${i + 1}`;
                  const url = typeof f === 'string' ? f : (f.url || f.path || null);
                  return (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {url ? (
                        <a href={url} download style={{ color: '#1a2e4a' }}>{name}</a>
                      ) : (
                        <span>{name}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
